/**
 * Driver Delivery Wizard DSL — multi-step delivery/exception workflow.
 *
 * Steps:
 *   1. Outcome (success / failure) — card-select
 *   2. Reason code — card-select (options loaded dynamically by tripType)
 *   3. Photos — photo capture (optional)
 *   4. Signature — signature capture (optional)
 *   5. Notes — textarea + summary + submit
 *
 * Exception mode skips step 1 and uses exception types instead of reason codes.
 */

import type { ViewTemplateDSL, WizardStepDSL } from '@/lib/use-view-template';

// ─── Delivery wizard steps ───────────────────────────────────────────────────

const OUTCOME_STEP: WizardStepDSL = {
  id: 'outcome',
  label: 'Outcome',
  icon: 'check-circle',
  description: 'What is the delivery outcome?',
  autoAdvance: true,
  fields: [
    {
      field: 'outcome',
      label: 'Delivery Outcome',
      type: 'card-select',
      renderAs: 'card-grid',
      required: true,
      options: [
        { label: 'Success',  value: 'success', description: 'Delivered normally',  icon: 'check-circle', color: 'green' },
        { label: 'Failed',   value: 'failure', description: 'Could not deliver',   icon: 'x-circle',     color: 'red' },
      ],
    },
  ],
};

const REASON_STEP: WizardStepDSL = {
  id: 'reason',
  label: 'Reason',
  icon: 'list',
  description: 'Select a reason code',
  autoAdvance: true,
  fields: [
    {
      field: 'reasonCode',
      label: 'Reason Code',
      type: 'card-select',
      renderAs: 'card-list',
      required: true,
      // Options are injected dynamically based on outcome + tripType
      options: [],
    },
  ],
};

const EXCEPTION_REASON_STEP: WizardStepDSL = {
  id: 'reason',
  label: 'Issue Type',
  icon: 'alert-triangle',
  description: 'What type of issue?',
  autoAdvance: true,
  fields: [
    {
      field: 'exceptionType',
      label: 'Issue Type',
      type: 'card-select',
      renderAs: 'card-list',
      required: true,
      // Options are set from EXCEPTION_TYPES config
      options: [],
    },
  ],
};

const PHOTOS_STEP: WizardStepDSL = {
  id: 'photos',
  label: 'Photos',
  icon: 'camera',
  optional: true,
  skipLabel: 'Skip Photos',
  nextLabel: 'Next',
  fields: [
    {
      field: 'photos',
      label: 'Proof of Delivery photos',
      type: 'photo',
      maxItems: 5,
      captureMode: 'both',
    },
  ],
};

const SIGNATURE_STEP: WizardStepDSL = {
  id: 'signature',
  label: 'Signature',
  icon: 'pen-tool',
  optional: true,
  fields: [
    {
      field: 'signature',
      label: 'Receiver signature',
      type: 'signature',
    },
  ],
};

const NOTES_STEP: WizardStepDSL = {
  id: 'notes',
  label: 'Notes & Submit',
  icon: 'send',
  fields: [
    {
      field: 'notes',
      label: 'Additional notes',
      type: 'textarea',
      placeholder: 'Enter any additional notes here...',
    },
  ],
};

// ─── Full wizard DSL ─────────────────────────────────────────────────────────

export const DELIVERY_WIZARD_DSL: ViewTemplateDSL = {
  wizard: {
    steps: [OUTCOME_STEP, REASON_STEP, PHOTOS_STEP, SIGNATURE_STEP, NOTES_STEP],
    submitLabel: 'Confirm Delivery',
    showStepIndicator: true,
  },
};

export const EXCEPTION_WIZARD_DSL: ViewTemplateDSL = {
  wizard: {
    steps: [EXCEPTION_REASON_STEP, PHOTOS_STEP, SIGNATURE_STEP, NOTES_STEP],
    submitLabel: 'Submit Report',
    showStepIndicator: true,
  },
};

// ─── Re-export step definitions for dynamic option injection ─────────────────

export {
  OUTCOME_STEP,
  REASON_STEP,
  EXCEPTION_REASON_STEP,
  PHOTOS_STEP,
  SIGNATURE_STEP,
  NOTES_STEP,
};
